"use client";

/**
 * Отменяемое удаление в стиле Gmail (пункт 15 бэклога аудита 2026-07).
 *
 * Паттерн: вызывающий код УБИРАЕТ элемент из UI сразу (optimistic), но реальный
 * DELETE уходит на сервер только через 5 секунд. Всё это время висит тост с
 * кнопкой «Отменить»: клик возвращает элемент (restore) — сервер даже не
 * узнаёт об удалении.
 *
 * Надёжность:
 *  - закрытие вкладки/переход на другой сайт в окно 5с → commit выполняется в
 *    pagehide (fetch может не успеть при жёстком закрытии — тогда элемент
 *    просто останется, что безопаснее случайной потери данных);
 *  - ошибка commit'а → restore() + красный тост (элемент возвращается);
 *  - повторные вызовы idempotent-защищены флагом done.
 */

import { showActionToast, showToast } from "./Toast";

const UNDO_WINDOW_MS = 5000;

export function undoableDelete(opts: {
  /** Текст тоста, например: «Задача удалена». */
  message: string;
  /** Реальное удаление на сервере (выполнится через 5с, если не отменили). */
  commit: () => Promise<unknown>;
  /** Вернуть элемент в UI (при «Отменить» или при ошибке commit'а). */
  restore: () => void;
  /** Текст ошибки, если commit упал (по умолчанию «Не удалось удалить»). */
  errorMessage?: string;
}): void {
  let done = false;

  const commitOnce = () => {
    if (done) return;
    done = true;
    window.removeEventListener("pagehide", commitOnce);
    opts.commit().catch(() => {
      opts.restore();
      showToast(opts.errorMessage ?? "Не удалось удалить", "error");
    });
  };

  const timer = window.setTimeout(commitOnce, UNDO_WINDOW_MS);
  window.addEventListener("pagehide", commitOnce);

  showActionToast(opts.message, {
    label: "Отменить",
    onAction: () => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      window.removeEventListener("pagehide", commitOnce);
      opts.restore();
    },
  }, { durationMs: UNDO_WINDOW_MS });
}
